//@ts-nocheck
import "chart.js/auto";
import { ChartOptions } from "chart.js";
import currency from "currency.js";
import { useCallback } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import {
  BORDER_COLORS,
  MODAL_COLORS,
  TEXT_COLORS,
} from "../../../constants/design/colors";
import { FONT_STYLE } from "../../../constants/design/fontSize";
import { SPACING } from "../../../constants/design/spacing";
import { drawCircle, drawRoundRect } from "../../../utils/canvas";
import { AssetResponse } from "../../../utils/asset";
import { upColor, downColor } from "./helpers";
import { IDrawBidAskProps } from "./types";

interface IDepthChartCanvasProps {
  data: any;
  options: ChartOptions<"line">;
  asset: AssetResponse;
  precision: () => any;
}

function DepthChartCanvas({
  data,
  options,
  asset,
  precision,
}: IDepthChartCanvasProps) {
  const { t } = useTranslation("app", { keyPrefix: "DepthChart" });

  const drawLabel = useCallback(
    (
      chart: any,
      ctx: CanvasRenderingContext2D,
      x: number,
      y: number,
      values: {
        label: string;
        value: string;
      }[],
      side: "left" | "right"
    ) => {
      const valueHeight = 20;

      const rectPadding = SPACING.three;
      const rectHeight =
        rectPadding * 2 + values.length * (valueHeight + SPACING.two);
      const rectWidth = 240;
      const rectX =
        side === "left" ? x - rectWidth - SPACING.two : x + SPACING.two;
      let rectY = y - rectHeight / 2;
      if (rectY < SPACING.two) {
        rectY = SPACING.two;
      } else if (rectY + rectHeight + SPACING.two > chart.height) {
        // v3: chart.height (not chart.chart.height)
        rectY = chart.height - SPACING.two - rectHeight;
      }

      ctx.save();
      ctx.fillStyle = MODAL_COLORS.one;
      ctx.strokeStyle = BORDER_COLORS.one;
      ctx.beginPath();
      drawRoundRect(
        ctx,
        rectX,
        rectY,
        rectWidth,
        rectHeight,
        {
          upperLeft: 8,
          upperRight: 8,
          lowerLeft: 8,
          lowerRight: 8,
        },
        true
      );
      ctx.restore();

      let labelY = rectY + rectPadding;
      const labelX = rectX + rectPadding;
      values.forEach(({ label, value }) => {
        ctx.save();
        ctx.textBaseline = "top";

        ctx.font = `${FONT_STYLE.label.two.fontSize} BaseFont`;
        ctx.fillStyle = TEXT_COLORS.three;
        ctx.textAlign = "left";
        ctx.fillText(label, labelX, labelY);

        const valueX = rectX + rectWidth - rectPadding;
        ctx.font = `${FONT_STYLE.data.three.fontSize} BaseFont`;
        ctx.fillStyle = TEXT_COLORS.one;
        ctx.textAlign = "right";
        ctx.fillText(value, valueX, labelY);

        labelY += valueHeight + SPACING.three;

        ctx.restore();
      });
    },
    []
  );

  const drawPricePoint = useCallback(
    (
      chart: any,
      bidAsk: IDrawBidAskProps,
      priceImpact: number,
      type: "bid" | "ask"
    ) => {
      const ctx: CanvasRenderingContext2D = chart.ctx;

      const { top, bottom, right, left } = chart.chartArea;

      ctx.save();
      ctx.setLineDash([5, 5]);
      ctx.lineWidth = 1;
      ctx.strokeStyle =
        type === "bid" ? upColor(0.4) : downColor(0.4);
      ctx.globalCompositeOperation = "source-over";

      ctx.beginPath();
      ctx.moveTo(bidAsk.x, top);
      ctx.lineTo(bidAsk.x, bottom);
      ctx.moveTo(left, bidAsk.y);
      ctx.lineTo(right, bidAsk.y);
      ctx.stroke();

      ctx.restore();

      const circleColorPrimary =
        type === "bid" ? upColor() : downColor();
      const circleColorSecondary =
        type === "bid" ? upColor(0.24) : downColor(0.24);

      drawCircle(
        ctx,
        bidAsk.x,
        bidAsk.y,
        2,
        circleColorPrimary,
        circleColorPrimary,
        0
      );

      drawCircle(
        ctx,
        bidAsk.x,
        bidAsk.y,
        4,
        circleColorSecondary,
        circleColorSecondary,
        0
      );

      ctx.save();
      ctx.textBaseline = "top";

      ctx.font = `${FONT_STYLE.data.three.fontSize} BaseFont`;
      ctx.fillStyle = circleColorPrimary;
      ctx.textAlign = "center";
      ctx.fillText(
        currency(bidAsk.price, {
          precision: precision().price_precision,
        }).format(),
        bidAsk.x,
        chart.chartArea.bottom + 12
      );

      ctx.textAlign = "left";
      ctx.fillText(
        bidAsk.size.toFixed(precision().amount_precision),
        chart.chartArea.right + 10,
        bidAsk.y - 6
      );

      ctx.restore();

      drawLabel(
        chart,
        ctx,
        bidAsk.x,
        bidAsk.y,
        [
          {
            label: t("price"),
            value: currency(bidAsk.price, {
              precision: precision().price_precision,
            }).format(),
          },
          {
            label: `${type === "bid" ? t("buy") : t("sell")} ${t(
              "size"
            )} (${asset})`,
            value: currency(bidAsk.size, { symbol: "" }).format(),
          },
          {
            label: t("total_cost"),
            value: currency(bidAsk.totalCost, {
              precision: precision().price_precision,
            }).format(),
          },
          {
            label: t("price_impact"),
            value: `${(priceImpact * 100).toFixed(2)}%`,
          },
        ],
        type === "bid" ? "right" : "left"
      );
    },
    [asset, drawLabel, precision, t]
  );

  return (
    <Line
      data={data}
      options={options}
      plugins={[
        {
          afterDraw: (c: any) => {
            // v3: c is the chart instance directly (not c.chart)
            const hoveredDatasetIndex = c.data.datasets.findIndex(
              (dataset: any) => dataset.label === "hovered"
            );
            const hoveredDataset: (number | null)[] =
              c.data.datasets[hoveredDatasetIndex].data;

            const bidsDatasetIndex = c.data.datasets.findIndex(
              (dataset: any) => dataset.label === "bids"
            );
            const bidsDataset: (number | null)[] =
              c.data.datasets[bidsDatasetIndex].data;

            const [hoveredDataIndex] = hoveredDataset
              .filter((v) => v !== null)
              .map((v) => hoveredDataset.indexOf(v));

            if (hoveredDataIndex !== undefined) {
              const { labels: chartLabels } = c.data;
              const price = chartLabels[hoveredDataIndex];
              const size = hoveredDataset[hoveredDataIndex];

              const hoveredTotalCostsDatasetIndex =
                c.data.datasets.findIndex(
                  (dataset: any) =>
                    dataset.label === "hoveredTotalCostsData"
                );
              const hoveredTotalCostsDataset: (number | null)[] =
                c.data.datasets[hoveredTotalCostsDatasetIndex].data;
              const totalCosts =
                hoveredTotalCostsDataset[hoveredDataIndex];

              // The first 0 in the bids dataset is the middle point
              const midIndex = bidsDataset.indexOf(0);

              const chartMidPrice = chartLabels[midIndex];
              const priceImpact =
                Math.abs(price - chartMidPrice) / chartMidPrice;

              const type = hoveredDataIndex > midIndex ? "ask" : "bid";

              // v3: element positions are .x/.y directly (not ._view)
              const metadata = c.getDatasetMeta(hoveredDatasetIndex);
              const hoverX = metadata.data[hoveredDataIndex]?.x;
              const hoverY = metadata.data[hoveredDataIndex]?.y;

              if (hoverX !== undefined && hoverY !== undefined) {
                drawPricePoint(
                  c,
                  {
                    price,
                    x: hoverX,
                    y: hoverY,
                    size: size || 0,
                    totalCost: totalCosts || 0,
                  },
                  priceImpact,
                  type
                );
              }
            }
          },
        },
      ]}
    />
  );
}

export default DepthChartCanvas;
